if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      hide(preventFocus = false) {
        const cartNotification =
          document.querySelector('cart-notification') ||
          document.querySelector('cart-drawer');

        if (cartNotification) {
          cartNotification.setActiveElement(this.openedBy);
        }

        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;

        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');

        const spinner = opener.querySelector('.loading__spinner');
        if (spinner) spinner.classList.remove('hidden');

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(
              responseText,
              'text/html'
            );

            const productElement = responseHTML.querySelector('product-info');

            if (!productElement) return;

            this.preprocessHTML(productElement);

            HTMLUpdateUtility.setInnerHTML(
              this.modalContent,
              productElement.outerHTML
            );

            this.setupColumnSelector();

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }

            if (window.ProductModel) {
              window.ProductModel.loadShopifyXR();
            }

            super.show(opener);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');

            const spinner = opener.querySelector('.loading__spinner');
            if (spinner) spinner.classList.add('hidden');
          });
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (
            classApplied.startsWith('color-') ||
            classApplied === 'gradient'
          ) {
            this.modalContent.classList.add(classApplied);
          }
        });

        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      setupColumnSelector() {
        const dataElement = this.querySelector(
          '[data-ob-quick-column-variants]'
        );

        if (!dataElement) return;

        let columns;

        try {
          columns = JSON.parse(dataElement.textContent);
        } catch (error) {
          console.error(
            'Could not load auxiliary column variants.',
            error
          );
          return;
        }

        const variants = [
          ...(columns['70'] || []).map((variant) => ({
            ...variant,
            size: '70',
          })),
          ...(columns['140'] || []).map((variant) => ({
            ...variant,
            size: '140',
          })),
        ];

        if (!variants.length) return;

        /*
         * =========================================================
         * IDIOMA / MERCADO
         * =========================================================
         */

        const hostname = window.location.hostname.toLowerCase();

        const rawLocale = (
          document.documentElement.lang ||
          window.Shopify?.locale ||
          'es'
        ).toLowerCase();

        let language = rawLocale.split('-')[0];

        if (hostname.endsWith('.pt')) {
          language = 'pt';
        } else if (
          hostname.endsWith('.fr') ||
          hostname.endsWith('.lu')
        ) {
          language = 'fr';
        } else if (hostname.endsWith('.it')) {
          language = 'it';
        } else if (hostname.endsWith('.se')) {
          language = 'sv';
        } else if (hostname.endsWith('.pl')) {
          language = 'pl';
        } else if (
          hostname.endsWith('.de') ||
          hostname.endsWith('.at')
        ) {
          language = 'de';
        } else if (hostname.endsWith('.ro')) {
          language = 'ro';
        } else if (hostname.endsWith('.nl')) {
          language = 'nl';
        } else if (hostname.endsWith('.cz')) {
          language = 'cs';
        } else if (hostname.endsWith('.hu')) {
          language = 'hu';
        } else if (hostname.endsWith('.dk')) {
          language = 'da';
        } else if (hostname.endsWith('.fi')) {
          language = 'fi';
        } else if (hostname.endsWith('.ee')) {
          language = 'et';
        } else if (hostname.endsWith('.lv')) {
          language = 'lv';
        } else if (hostname.endsWith('.lt')) {
          language = 'lt';
        } else if (hostname.endsWith('.gr')) {
          language = 'el';
        } else if (hostname.endsWith('.co.uk')) {
          language = 'en';
        } else if (hostname.endsWith('.ad')) {
          language = 'ca';
        } else if (hostname.endsWith('.es')) {
          language = 'es';
        }

        /*
         * =========================================================
         * TRADUCCIONES
         * =========================================================
         */

        const translations = {
          es: {
            label: 'Columna auxiliar',
            none: 'Sin columna',
            add: 'Añadir columna',
            soldOut: 'Agotada',
            colors: {
              blanco: 'Blanco',
              'blanco nordico': 'Blanco nórdico',
              'roble smoky': 'Roble Smoky',
              estepa: 'Estepa',
              'fresno tea': 'Fresno Tea',
              grafito: 'Grafito',
            },
          },

          pt: {
            label: 'Coluna auxiliar',
            none: 'Sem coluna',
            add: 'Adicionar coluna',
            soldOut: 'Esgotada',
            colors: {
              blanco: 'Branco',
              'blanco nordico': 'Branco nórdico',
              'roble smoky': 'Carvalho Smoky',
              estepa: 'Estepa',
              'fresno tea': 'Freixo Tea',
              grafito: 'Grafite',
            },
          },

          fr: {
            label: 'Colonne auxiliaire',
            none: 'Sans colonne',
            add: 'Ajouter une colonne',
            soldOut: 'Épuisée',
            colors: {
              blanco: 'Blanc',
              'blanco nordico': 'Blanc nordique',
              'roble smoky': 'Chêne Smoky',
              estepa: 'Estepa',
              'fresno tea': 'Frêne Tea',
              grafito: 'Graphite',
            },
          },

          it: {
            label: 'Colonna ausiliaria',
            none: 'Senza colonna',
            add: 'Aggiungi colonna',
            soldOut: 'Esaurita',
            colors: {
              blanco: 'Bianco',
              'blanco nordico': 'Bianco nordico',
              'roble smoky': 'Rovere Smoky',
              estepa: 'Estepa',
              'fresno tea': 'Frassino Tea',
              grafito: 'Grafite',
            },
          },

          de: {
            label: 'Zusatzschrank',
            none: 'Ohne Zusatzschrank',
            add: 'Zusatzschrank hinzufügen',
            soldOut: 'Ausverkauft',
            colors: {
              blanco: 'Weiß',
              'blanco nordico': 'Nordisches Weiß',
              'roble smoky': 'Smoky Eiche',
              estepa: 'Estepa',
              'fresno tea': 'Tea Esche',
              grafito: 'Graphit',
            },
          },

          pl: {
            label: 'Dodatkowy słupek',
            none: 'Bez dodatkowego słupka',
            add: 'Dodaj słupek',
            soldOut: 'Niedostępny',
            colors: {
              blanco: 'Biały',
              'blanco nordico': 'Biały nordycki',
              'roble smoky': 'Dąb Smoky',
              estepa: 'Estepa',
              'fresno tea': 'Jesion Tea',
              grafito: 'Grafit',
            },
          },

          ro: {
            label: 'Coloană auxiliară',
            none: 'Fără coloană',
            add: 'Adaugă coloană',
            soldOut: 'Stoc epuizat',
            colors: {
              blanco: 'Alb',
              'blanco nordico': 'Alb nordic',
              'roble smoky': 'Stejar Smoky',
              estepa: 'Estepa',
              'fresno tea': 'Frasin Tea',
              grafito: 'Grafit',
            },
          },

          nl: {
            label: 'Extra kolomkast',
            none: 'Zonder kolomkast',
            add: 'Kolomkast toevoegen',
            soldOut: 'Uitverkocht',
            colors: {
              blanco: 'Wit',
              'blanco nordico': 'Noords wit',
              'roble smoky': 'Smoky eiken',
              estepa: 'Estepa',
              'fresno tea': 'Essen Tea',
              grafito: 'Grafiet',
            },
          },

          sv: {
            label: 'Extra högskåp',
            none: 'Utan extra högskåp',
            add: 'Lägg till högskåp',
            soldOut: 'Slutsåld',
            colors: {
              blanco: 'Vit',
              'blanco nordico': 'Nordisk vit',
              'roble smoky': 'Smoky ek',
              estepa: 'Estepa',
              'fresno tea': 'Ask Tea',
              grafito: 'Grafit',
            },
          },

          cs: {
            label: 'Doplňková skříňka',
            none: 'Bez doplňkové skříňky',
            add: 'Přidat doplňkovou skříňku',
            soldOut: 'Vyprodáno',
            colors: {
              blanco: 'Bílá',
              'blanco nordico': 'Severská bílá',
              'roble smoky': 'Dub Smoky',
              estepa: 'Estepa',
              'fresno tea': 'Jasan Tea',
              grafito: 'Grafit',
            },
          },

          hu: {
            label: 'Kiegészítő oszlop',
            none: 'Oszlop nélkül',
            add: 'Kiegészítő oszlop hozzáadása',
            soldOut: 'Elfogyott',
            colors: {
              blanco: 'Fehér',
              'blanco nordico': 'Nordikus fehér',
              'roble smoky': 'Smoky tölgy',
              estepa: 'Estepa',
              'fresno tea': 'Tea kőris',
              grafito: 'Grafit',
            },
          },

          da: {
            label: 'Ekstra højskab',
            none: 'Uden ekstra højskab',
            add: 'Tilføj højskab',
            soldOut: 'Udsolgt',
            colors: {
              blanco: 'Hvid',
              'blanco nordico': 'Nordisk hvid',
              'roble smoky': 'Smoky eg',
              estepa: 'Estepa',
              'fresno tea': 'Ask Tea',
              grafito: 'Grafit',
            },
          },

          fi: {
            label: 'Lisäkaappi',
            none: 'Ilman lisäkaappia',
            add: 'Lisää lisäkaappi',
            soldOut: 'Loppuunmyyty',
            colors: {
              blanco: 'Valkoinen',
              'blanco nordico': 'Pohjoismainen valkoinen',
              'roble smoky': 'Smoky tammi',
              estepa: 'Estepa',
              'fresno tea': 'Saarni Tea',
              grafito: 'Grafiitti',
            },
          },

          et: {
            label: 'Lisakapp',
            none: 'Ilma lisakapita',
            add: 'Lisa kapp',
            soldOut: 'Läbi müüdud',
            colors: {
              blanco: 'Valge',
              'blanco nordico': 'Põhjamaine valge',
              'roble smoky': 'Smoky tamm',
              estepa: 'Estepa',
              'fresno tea': 'Tea saar',
              grafito: 'Grafiit',
            },
          },

          lv: {
            label: 'Papildu skapis',
            none: 'Bez papildu skapja',
            add: 'Pievienot papildu skapi',
            soldOut: 'Izpārdots',
            colors: {
              blanco: 'Balts',
              'blanco nordico': 'Ziemeļnieciski balts',
              'roble smoky': 'Smoky ozols',
              estepa: 'Estepa',
              'fresno tea': 'Tea osis',
              grafito: 'Grafīts',
            },
          },

          lt: {
            label: 'Papildoma spintelė',
            none: 'Be papildomos spintelės',
            add: 'Pridėti papildomą spintelę',
            soldOut: 'Išparduota',
            colors: {
              blanco: 'Balta',
              'blanco nordico': 'Šiaurietiška balta',
              'roble smoky': 'Smoky ąžuolas',
              estepa: 'Estepa',
              'fresno tea': 'Tea uosis',
              grafito: 'Grafitas',
            },
          },

          el: {
            label: 'Βοηθητική στήλη',
            none: 'Χωρίς βοηθητική στήλη',
            add: 'Προσθήκη βοηθητικής στήλης',
            soldOut: 'Εξαντλήθηκε',
            colors: {
              blanco: 'Λευκό',
              'blanco nordico': 'Σκανδιναβικό λευκό',
              'roble smoky': 'Καπνιστή δρυς',
              estepa: 'Estepa',
              'fresno tea': 'Δεσποτάκι Tea',
              grafito: 'Γραφίτης',
            },
          },

          en: {
            label: 'Auxiliary cabinet',
            none: 'No auxiliary cabinet',
            add: 'Add auxiliary cabinet',
            soldOut: 'Sold out',
            colors: {
              blanco: 'White',
              'blanco nordico': 'Nordic White',
              'roble smoky': 'Smoky Oak',
              estepa: 'Estepa',
              'fresno tea': 'Tea Ash',
              grafito: 'Graphite',
            },
          },

          ca: {
            label: 'Columna auxiliar',
            none: 'Sense columna',
            add: 'Afegir columna',
            soldOut: 'Esgotada',
            colors: {
              blanco: 'Blanc',
              'blanco nordico': 'Blanc nòrdic',
              'roble smoky': 'Roure Smoky',
              estepa: 'Estepa',
              'fresno tea': 'Freixe Tea',
              grafito: 'Grafit',
            },
          },
        };

        const copy =
          translations[language] ||
          translations.es;

        /*
         * =========================================================
         * CONTENEDOR
         * =========================================================
         */

        const wrapper = document.createElement('div');

        wrapper.className =
          'ob-quick-column product-form__input product-form__input--dropdown';

        /*
         * =========================================================
         * LABEL
         * =========================================================
         */

        const label = document.createElement('label');

        label.className = 'form__label';
        label.htmlFor = `ObQuickColumn-${this.id}`;
        label.textContent = copy.label;

        /*
         * =========================================================
         * SELECT
         * =========================================================
         */

        const selectWrapper = document.createElement('div');

        selectWrapper.className = 'select';

        const select = document.createElement('select');

        select.id = label.htmlFor;
        select.className = 'select__select';

        select.setAttribute(
          'data-ob-quick-column',
          ''
        );

        select.setAttribute(
          'aria-label',
          copy.label
        );

        /*
         * =========================================================
         * SIN COLUMNA
         * =========================================================
         */

        const emptyOption =
          document.createElement('option');

        emptyOption.value = '';
        emptyOption.textContent = copy.none;
        emptyOption.dataset.label = copy.none;

        /*
         * Evita que otros scripts de traducción vuelvan
         * a modificar estas opciones.
         */
        emptyOption.dataset.obColumnLabel = 'true';

        select.appendChild(emptyOption);

        /*
         * =========================================================
         * VARIANTES
         * =========================================================
         */

        variants.forEach((variant) => {
          const option =
            document.createElement('option');

          const normalizedTitle = variant.title
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .trim()
            .toLowerCase();

          const color =
            copy.colors[normalizedTitle] ||
            variant.title;

          const optionLabel =
            `${copy.add} ${variant.size} cm ` +
            `(+${variant.priceLabel}) ${color}`;

          option.value = String(variant.id);

          option.textContent = variant.available
            ? optionLabel
            : `${optionLabel} — ${copy.soldOut}`;

          option.dataset.label = optionLabel;
          option.dataset.obColumnLabel = 'true';

          option.disabled = !variant.available;

          select.appendChild(option);
        });

        /*
         * =========================================================
         * ICONO
         * =========================================================
         */

        const icon = document.createElementNS(
          'http://www.w3.org/2000/svg',
          'svg'
        );

        icon.setAttribute(
          'class',
          'icon icon-caret'
        );

        icon.setAttribute(
          'viewBox',
          '0 0 10 6'
        );

        icon.setAttribute(
          'aria-hidden',
          'true'
        );

        const path = document.createElementNS(
          'http://www.w3.org/2000/svg',
          'path'
        );

        path.setAttribute(
          'fill-rule',
          'evenodd'
        );

        path.setAttribute(
          'clip-rule',
          'evenodd'
        );

        path.setAttribute(
          'd',
          'M9.354.646a.5.5 0 0 1 0 .708l-4 4a.5.5 0 0 1-.708 0l-4-4a.5.5 0 1 1 .708-.708L5 4.293 8.646.646a.5.5 0 0 1 .708 0Z'
        );

        path.setAttribute(
          'fill',
          'currentColor'
        );

        icon.appendChild(path);

        selectWrapper.append(
          select,
          icon
        );

        wrapper.append(
          label,
          selectWrapper
        );

        /*
         * =========================================================
         * SUSTITUIR INFINITE OPTIONS EN QUICK ADD
         * =========================================================
         */

        const appContainer =
          this.modalContent.querySelector(
            '#infiniteoptions-container'
          );

        const appBlock =
          appContainer?.closest(
            '.shopify-app-block'
          ) || appContainer;

        if (appBlock) {
          appBlock.replaceWith(wrapper);
          return;
        }

        /*
         * =========================================================
         * DESPUÉS DEL SELECTOR DE VARIANTE
         * =========================================================
         */

        const variantPicker =
          this.modalContent.querySelector(
            'variant-selects'
          );

        if (variantPicker) {
          variantPicker.insertAdjacentElement(
            'afterend',
            wrapper
          );
          return;
        }

        /*
         * =========================================================
         * ANTES DEL FORMULARIO
         * =========================================================
         */

        const productForm =
          this.modalContent.querySelector(
            'product-form'
          );

        productForm?.insertAdjacentElement(
          'beforebegin',
          wrapper
        );
      }

      removeDOMElements(productElement) {
        const pickupAvailability =
          productElement.querySelector(
            'pickup-availability'
          );

        if (pickupAvailability) {
          pickupAvailability.remove();
        }

        const productModal =
          productElement.querySelector(
            'product-modal'
          );

        if (productModal) {
          productModal.remove();
        }

        const modalDialog =
          productElement.querySelectorAll(
            'modal-dialog'
          );

        if (modalDialog) {
          modalDialog.forEach(
            (modal) => modal.remove()
          );
        }
      }

      preventDuplicatedIDs(productElement) {
        const sectionId =
          productElement.dataset.section;

        const oldId = sectionId;
        const newId =
          `quickadd-${sectionId}`;

        productElement.innerHTML =
          productElement.innerHTML.replaceAll(
            oldId,
            newId
          );

        Array.from(
          productElement.attributes
        ).forEach((attribute) => {
          if (
            attribute.value.includes(oldId)
          ) {
            productElement.setAttribute(
              attribute.name,
              attribute.value.replace(
                oldId,
                newId
              )
            );
          }
        });

        productElement.dataset.originalSection =
          sectionId;
      }

      removeGalleryListSemantic(
        productElement
      ) {
        const galleryList =
          productElement.querySelector(
            '[id^="Slider-Gallery"]'
          );

        if (!galleryList) return;

        galleryList.setAttribute(
          'role',
          'presentation'
        );

        galleryList
          .querySelectorAll('[id^="Slide-"]')
          .forEach((li) =>
            li.setAttribute(
              'role',
              'presentation'
            )
          );
      }

      updateImageSizes(productElement) {
        const product =
          productElement.querySelector(
            '.product'
          );

        const desktopColumns =
          product?.classList.contains(
            'product--columns'
          );

        if (!desktopColumns) return;

        const mediaImages =
          product.querySelectorAll(
            '.product__media img'
          );

        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, ' +
          '(min-width: 750px) calc((100vw - 11.5rem) / 2), ' +
          'calc(100vw - 4rem)';

        if (
          product.classList.contains(
            'product--medium'
          )
        ) {
          mediaImageSizes =
            mediaImageSizes.replace(
              '715px',
              '605px'
            );
        } else if (
          product.classList.contains(
            'product--small'
          )
        ) {
          mediaImageSizes =
            mediaImageSizes.replace(
              '715px',
              '495px'
            );
        }

        mediaImages.forEach((img) =>
          img.setAttribute(
            'sizes',
            mediaImageSizes
          )
        );
      }
    }
  );
}